import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import { closeConnectionModal, fetchAllPublicKeys } from '../../integrationsSlice'
import { fetchAllSecrets } from '../../../../features/aws/awsSecretsManagerSlice'
import { baseUrl } from '../../../../modules/urls'
import { getAxiosDefaultConfig } from '../../../../modules/utils'
import { parseIntegrationTags } from '../../integrationTags'

const feedonomicsConnectUrl = `${baseUrl}/auth/feedonomics/connect`

interface FormValues {
  api_key: string
  /** FeedAMP API `client` header (integer). Required for order / fulfillment MCP tools. */
  client_id: string
}

interface Channel {
  id: string
  ppKeys?: Array<{ keyId: string; keyValue: string; tags?: unknown }>
}

interface Props {
  ownerId: string | number
  retrieveAccounts: () => Promise<void>
  channel?: Channel
  allPublicKeys?: Array<{ keyId: string; keyValue?: string; tags?: unknown }>
}

type ViewMode = 'manage' | 'connect' | 'update'

function isFeedonomicsKeyId(keyId: string | undefined | null): boolean {
  if (keyId == null || keyId === '') return false
  const parts = String(keyId).split('--')
  return parts.length === 2 && parts[1] === 'feedonomics'
}

function collectFeedonomicsRows(
  channel: Channel | undefined,
  allPublicKeys: Array<{ keyId: string; tags?: unknown }> | undefined
): Array<{ keyId: string; apiKeyPreview: string; feedampClientId: string; legacyUsername: string }> {
  const map = new Map<string, { keyId: string; tags?: unknown }>()
  ;(allPublicKeys || []).forEach((row) => {
    if (!row?.keyId || !isFeedonomicsKeyId(row.keyId)) return
    map.set(row.keyId, row)
  })
  ;(channel?.ppKeys || []).forEach((row: any) => {
    if (row?.keyId && isFeedonomicsKeyId(row.keyId) && !map.has(row.keyId)) {
      map.set(row.keyId, row)
    }
  })
  return Array.from(map.values()).map((row) => {
    const tm = parseIntegrationTags(row.tags)
    return {
      keyId: row.keyId,
      apiKeyPreview: String(tm.feedonomics_api_key_preview || ''),
      feedampClientId: String(tm.feedamp_client_id || ''),
      legacyUsername: String(tm.username || ''),
    }
  })
}

export default function FeedonomicsConnectionModalContent({
  ownerId,
  retrieveAccounts,
  channel,
  allPublicKeys,
}: Props) {
  const dispatch = useDispatch<any>()
  const closeModal = () => dispatch((closeConnectionModal as any)())

  const rows = useMemo(() => collectFeedonomicsRows(channel, allPublicKeys), [channel, allPublicKeys])
  const hasConnections = rows.length > 0

  const [viewMode, setViewMode] = useState<ViewMode>(hasConnections ? 'manage' : 'connect')
  const [showApiKey, setShowApiKey] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)

  useEffect(() => {
    setViewMode((cur) => {
      if (hasConnections && cur === 'connect') return 'manage'
      if (!hasConnections && cur === 'manage') return 'connect'
      return cur
    })
  }, [hasConnections])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { api_key: '', client_id: '' },
  })

  const onSubmit = async ({ api_key, client_id: clientIdField }: FormValues) => {
    setConnectError(null)
    const isUpdate = viewMode === 'update'
    const clientTrim = String(clientIdField || '').trim()
    const client_id =
      clientTrim === ''
        ? undefined
        : (() => {
            const n = Number(clientTrim)
            return Number.isFinite(n) && n > 0 && Math.floor(n) === n ? n : undefined
          })()
    if (clientTrim !== '' && client_id === undefined) {
      setConnectError('FeedAMP client ID must be a positive whole number, or leave blank to keep the existing value when updating.')
      return
    }
    try {
      await axios.post(
        feedonomicsConnectUrl,
        {
          api_key: api_key.trim(),
          ...(client_id != null ? { client_id } : {}),
          ...(isUpdate ? { update: true } : {}),
        },
        getAxiosDefaultConfig()
      )
      await retrieveAccounts()
      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
        dispatch((fetchAllSecrets as any)({ ownerId }))
      }
      reset()
      setViewMode('manage')
      dispatch((closeConnectionModal as any)())
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        'Failed to connect Feedonomics. Please check your API key and try again.'
      setConnectError(msg)
    }
  }

  const instructionsBlock = (
    <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4 tw-mb-5 tw-text-sm tw-text-blue-900">
      <p className="tw-mb-2 tw-font-semibold">Feedonomics API key</p>
      <p className="tw-mb-2">
        Paste your Feedonomics authentication token (API key). PayHelm sends it as the{' '}
        <code className="tw-text-xs">x-api-key</code> header for catalog and export API calls. For the PayHelm AI
        agent (orders, fulfillments, refunds), optionally add your <strong>FeedAMP client ID</strong> (integer from
        Feedonomics — same value used as the <code className="tw-text-xs">client</code> header in the FeedAMP API).
      </p>
      <p className="tw-mb-2 tw-text-xs tw-text-blue-800">
        PayHelm stores your API key only in the encrypted credential store, used solely for Feedonomics API access.
      </p>
      <p className="tw-mb-0">
        See the{' '}
        <a
          href="https://docs.feedonomics.com/developer/api-reference/rest/authentication-api/generate-authentication-token"
          target="_blank"
          rel="noopener noreferrer"
          className="tw-text-blue-600 tw-underline hover:tw-text-blue-800"
        >
          Feedonomics authentication API
          <FontAwesomeIcon icon={faExternalLinkAlt} className="tw-ml-1 tw-text-xs" />
        </a>{' '}
        for how to generate a token from your Feedonomics account.
      </p>
    </div>
  )

  const credentialForm = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="tw-mb-4">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">API key</label>
        <div className="tw-flex tw-items-center tw-gap-2">
          <input
            {...register('api_key', {
              required: 'API key is required',
              validate: (v) => v.trim().length > 0 || 'API key cannot be empty',
            })}
            type={showApiKey ? 'text' : 'password'}
            autoComplete="off"
            placeholder="Paste your Feedonomics API key"
            className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
          />
          <button
            type="button"
            className="tw-text-gray-500 hover:tw-text-gray-800 tw-text-lg"
            onClick={() => setShowApiKey((s) => !s)}
            aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
          >
            {showApiKey ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
        {errors.api_key && (
          <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.api_key.message}</p>
        )}
      </div>
      <div className="tw-mb-4">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          FeedAMP client ID <span className="tw-font-normal tw-text-gray-500">(optional)</span>
        </label>
        <input
          {...register('client_id')}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="e.g. 12345"
          className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
        />
        <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
          Required for marketplace order tools in the PayHelm AI agent. Leave blank on update to keep your saved
          client ID.
        </p>
      </div>

      {connectError && (
        <div className="tw-bg-red-50 tw-border tw-border-red-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-red-700 tw-mb-4">
          {connectError}
        </div>
      )}

      <div className="tw-flex tw-items-center tw-justify-end tw-space-x-2">
        <Button
          variant="secondary"
          onClick={() => {
            setConnectError(null)
            reset()
            if (viewMode === 'update') {
              setViewMode('manage')
            } else if (hasConnections) {
              setViewMode('manage')
            } else {
              closeModal()
            }
          }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-2" />
              {viewMode === 'update' ? 'Saving…' : 'Connecting…'}
            </>
          ) : viewMode === 'update' ? (
            'Save API key'
          ) : (
            'Connect'
          )}
        </Button>
      </div>
    </form>
  )

  if (viewMode === 'manage' && hasConnections) {
    const row = rows[0]
    const connectionLabel = row.apiKeyPreview || row.legacyUsername || '—'
    return (
      <div className="tw-p-6">
        <h2 className="tw-text-xl tw-mb-4">Manage Feedonomics</h2>

        <div className="tw-p-4 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-mb-6">
          <p className="tw-font-medium tw-text-gray-900">Connected</p>
          <p className="tw-text-sm tw-text-gray-600">
            {row.apiKeyPreview ? 'API key' : 'Account'}:{' '}
            <span className="tw-font-mono">{connectionLabel}</span>
          </p>
          {row.feedampClientId ? (
            <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
              FeedAMP client ID: <span className="tw-font-mono">{row.feedampClientId}</span>
            </p>
          ) : null}
          <p className="tw-text-xs tw-text-gray-500 tw-mt-2">
            To remove this connection, expand the integration card and use <strong>Disconnect</strong> on the account
            row. To change your API key, use <strong>Update API key</strong> below.
          </p>
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setConnectError(null)
              reset({
                api_key: '',
                client_id: row.feedampClientId || '',
              })
              setViewMode('update')
            }}
          >
            Update API key
          </Button>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-text-xl tw-mb-2">
        {viewMode === 'update' ? 'Update Feedonomics API key' : 'Connect Feedonomics'}
      </h2>
      {instructionsBlock}
      {credentialForm}
    </div>
  )
}
